import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useApp from "../../hooks/useApp";
import {
  CustomButton,
  CustomMessageModal,
  PoweredByLogo,
  SliderCard,
} from "../../components";
import useNavigateWithQuery from "../../hooks/useNavigateWithQuery";
import "./styles.scss";
import { useLocation } from "react-router-dom";

const FinancingCalculator = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const routeName = pathname.split("/")[1];
  const navigate = useNavigateWithQuery();
  const {
    changeLanguage,
    selectedLanguage,
    isRTL,
    tenantConfig,
    handlePreLoginCompleted,
  } = useApp();

  const [isLoading, setIsLoading] = useState(false);
  const [messageModalData, setMessageModalData] = useState(null);

  // Financing parameters
  const [financingAmount, setFinancingAmount] = useState(0);
  const [duration, setDuration] = useState(12); // in months
  const [monthlyInstallment, setMonthlyInstallment] = useState(0);

  // Configuration based on tenant
  const config = {
    snb: {
      minAmount: 500000,
      maxAmount: 800000,
      eligibleAmount: 800000,
    },
    alinma: {
      minAmount: 150000,
      maxAmount: 500000,
      eligibleAmount: 500000,
    },
  };

  // APR assumed at 7%
  const APR = 7;
  const monthlyRate = APR / 100 / 12;

  // Calculate monthly installment
  const calculateInstallment = (amount, months) => {
    if (amount <= 0 || months <= 0) return 0;

    const numerator = monthlyRate * amount;
    const denominator = 1 - Math.pow(1 + monthlyRate, -months);
    return numerator / denominator;
  };

  // Update installment when amount or duration changes
  useEffect(() => {
    const installment = calculateInstallment(financingAmount, duration);
    setMonthlyInstallment(installment);
  }, [financingAmount, duration]);

  // Initialize with eligible amount
  useEffect(() => {
    const eligibleAmount =
      tenantConfig?.slug === "snb"
        ? config.snb.eligibleAmount
        : config.alinma.eligibleAmount;
    setFinancingAmount(eligibleAmount);
  }, [tenantConfig?.slug]);

  const handleNavigation = () => {
    handlePreLoginCompleted();
    navigate(`/`);
  };

  const { minAmount, maxAmount } =
    tenantConfig?.slug === "snb" ? config.snb : config.alinma;

  return (
    <div className="register-container">
      <div className="content-container">
        <span
          className={`language-btn ${isRTL ? "language-btn-rtl" : ""}`}
          onClick={() =>
            changeLanguage(selectedLanguage?.value === "ar" ? "en" : "ar")
          }
        >
          {selectedLanguage?.value === "ar" ? "English" : "العربية"}
        </span>
        <img
          src={`${tenantConfig?.logo.content.default}`}
          alt="logo"
          className="logo"
        />
        <h1 className="title">
          {t(`${routeName}.congrats_eligible`, {
            amount: Number(maxAmount).toLocaleString(),
            currency: "SAR",
          })}
        </h1>
        <p className="description">{t(`${routeName}.set_finance_details`)}</p>

        <div className="body-container">
          <SliderCard
            value={financingAmount}
            cardHeading={"financing_amount"}
            valueSuffix={"currency_label"}
            onChange={(value) => setFinancingAmount(value)}
            minValue={minAmount}
            maxValue={maxAmount}
            labelSuffix={"currency_label"}
          />

          <SliderCard
            value={duration}
            cardHeading={"financing_duration"}
            valueSuffix={"month_label"}
            onChange={(value) => setDuration(value)}
            minValue={12}
            maxValue={60}
            step={12}
            marks={{
              12: "12",
              24: "24",
              36: "36",
              48: "48",
              60: "60",
            }}
            labelSuffix={"month_label"}
          />

          {/* Calculation Results */}
          <div className="loan-disbursement-modal-details-card">
            <div className="loan-disbursement-modal-details-card-item">
              <p className="loan-disbursement-modal-details-card-label">
                {t(`${routeName}.monthly_installment`)}
              </p>
              <p className="loan-disbursement-modal-details-card-value">
                {monthlyInstallment.toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })}{" "}
                SAR
              </p>
            </div>
          </div>
          <p className="disclaimer-description">
            {t(`${routeName}.disclaimer`)}
          </p>
          <PoweredByLogo />
        </div>
      </div>

      <div className="footer-container">
        <CustomButton
          isLoading={isLoading}
          text={t(`${routeName}.check_eligibility`)}
          onClick={handleNavigation}
        />
      </div>

      <CustomMessageModal
        show={!!messageModalData}
        onClickClose={() => setMessageModalData(null)}
        image={messageModalData?.msgImage}
        heading={messageModalData?.heading}
        description={messageModalData?.description}
      />
    </div>
  );
};

export default FinancingCalculator;
